// External Packages
import { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { BsArrowRightSquare, BsFilter } from "react-icons/bs";
import { BsArrowLeftSquare } from "react-icons/bs";
import { BsPlusLg } from "react-icons/bs";
import { MdRefresh } from "react-icons/md";
import { BiDownload } from "react-icons/bi";

// Internal Packages
import { DataContext } from "../../context/data-context";
import { timestampToString } from "../../util/timestamp-to-date";
import { arrayToCsv } from "../../util/arrayToCsv";
import LoadingSpinnerWrapper from "../UI/LoadingSpinnerWrapper";
import SearchBar from "../UI/SearchBar";
import PaymentCard from "./PaymentCard";
import Heading from "../UI/Heading";
import FilterMenu from "../UI/FilterMenu";

/*
  * All Payments Page
  This page displays all payments in the database.
*/

export default function PaymentsPage() {
  const navigate = useNavigate();
  const { payments, loadingData, fetchPayments } = useContext(DataContext);

  // Initialize States
  const [filteredPayments, setFilteredPayments] = useState(payments);

  // ========================================================== SEARCH HANDLER
  const searchHandler = (query) => {
    const filtered = payments.filter((payment) => {
      let match = true;
      const string = `${payment.customer.firstName} ${payment.customer.lastName} ${payment.customer.email} 
      ${payment.customer.phone} ${payment.description} ${payment.amount} ${payment.vehicle?.make}
      ${payment.vehicle?.model} ${payment.vehicle?.license} ${payment.vehicle?.year}
      ${payment.vehicle?.vin}`.toLowerCase();
      query.split(" ").forEach((word) => {
        if (!string.includes(word.toLowerCase())) match = false;
      });
      return match;
    });
    setFilteredPayments(filtered);
  };

  // ========================================================== FILTER HANDLER
  const filterOptions = [
    { label: "Status", options: ["All", "Succeeded", "Pending", "Refunded"] },
    { label: "Type", options: ["All", "Payment", "Invoice"] },
  ];
  const filterHandler = (filters) => {
    let filtered = [];
    payments.forEach((payment) => {
      let filter = true;
      if (
        filters.status !== "All" &&
        payment.status !== filters.status.toLowerCase()
      )
        filter = false;
      if (filters.type !== "All" && payment.type !== filters.type.toLowerCase())
        filter = false;
      if (filter) filtered.push(payment);
    });
    setFilteredPayments(filtered);
  };

  // ========================================================== DOWNLOAD CSV
  const downloadCSV = () => {
    const arr = [];
    filteredPayments.forEach((payment) => {
      arr.push({
        id: payment.id,
        customer: `${payment.customer.firstName} ${payment.customer.lastName}`,
        email: payment.customer.email,
        phone: payment.customer.phone,
        description: payment.description,
        amount: payment.amount,
        date: timestampToString(payment.date_created, "MM/dd/yyyy hh:mm a"),
        due: timestampToString(payment.due_date, "MM/dd/yyyy hh:mm a"),
        status: payment.status,
      });
    });
    arrayToCsv(arr, "payments");
  };

  return (
    <div className="container grid">
      <Heading title="Payments">
        <FilterMenu options={filterOptions} onSubmit={filterHandler}>
          <BsFilter className="text-2xl text-white hover:text-stone-400" />
        </FilterMenu>
        <BiDownload
          onClick={downloadCSV}
          className="text-xl text-white cursor-pointer hover:text-stone-400"
        />
        <BsPlusLg
          onClick={() => navigate("/payments/add")}
          className="text-lg text-white cursor-pointer hover:text-stone-400"
          title="Add Payment"
        />
        <MdRefresh
          onClick={fetchPayments}
          className="text-2xl text-white cursor-pointer hover:text-stone-400"
          title="Refresh"
        />
      </Heading>

      <SearchBar
        searchHandler={searchHandler}
        clearSearch={() => setFilteredPayments(payments)}
      />

      <div className="list">
        {loadingData ? (
          <LoadingSpinnerWrapper />
        ) : (
          <div className="grid gap-8">
            {filteredPayments.map((payment) => (
              <PaymentCard key={payment.id} payment={payment} />
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center justify-end space-x-4 text-sm text-stone-200">
        <p className="text-stone-400">
          Showing {filteredPayments.length} of {filteredPayments.length}{" "}
        </p>
        <BsArrowLeftSquare className="inline-block text-xl" />
        <BsArrowRightSquare className="inline-block text-xl" />
      </div>
    </div>
  );
}
